import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Heart, Package, Search, ShoppingBag, ShoppingCart, Sparkles, Tag, X } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { suggestProducts } from '@/api/shop';
import { useShopState } from '@/lib/shopStore';
import { categoryLook } from '@/lib/shopCategories';

export const CategoryGlyph = ({ category, className = 'h-14 w-14', iconClassName = 'h-7 w-7' }) => {
  const { Icon, bg, fg } = categoryLook(category);
  if (category?.image) {
    return <img src={category.image} alt="" className={`${className} rounded-2xl object-cover`} />;
  }
  return (
    <span className={`flex items-center justify-center rounded-2xl ${className}`} style={{ backgroundColor: bg, color: fg }}>
      <Icon className={iconClassName} strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
};

/* ---------------------------------------------------------------- search */

const ShopSearch = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const [q, setQ] = useState(pathname === '/shop/search' ? params.get('q') || '' : '');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (pathname === '/shop/search') setQ(params.get('q') || '');
  }, [pathname, params]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits(null);
      return undefined;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      suggestProducts(term, ctrl.signal)
        .then(setHits)
        .catch(() => {});
    }, 180);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  useEffect(() => {
    const onDown = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) go(`/shop/search?q=${encodeURIComponent(term)}`);
  };

  const hasHits = hits && (hits.products?.length || hits.brands?.length || hits.categories?.length);

  return (
    <form role="search" onSubmit={submit} className="relative min-w-0 flex-1" ref={boxRef}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
      <input
        type="search"
        enterKeyHint="search"
        aria-label="Search the shop"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search products & brands"
        className="lk-focus w-full rounded-xl border border-ink-200 bg-surface-muted py-2.5 pl-10 pr-10 text-base outline-none transition-colors focus:border-brand-secondary focus:bg-white md:text-sm [&::-webkit-search-cancel-button]:hidden"
      />
      {q && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setQ('')}
          className="lk-focus absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-ink-400 hover:text-ink-900"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {open && hasHits ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-ink-200 bg-white py-1.5 shadow-cardHover">
          {hits.categories?.map((c) => (
            <button key={`c-${c.slug}`} type="button" onClick={() => go(`/shop/c/${c.slug}`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-muted">
              <Tag className="h-4 w-4 text-ink-400" />
              <span className="flex-1 truncate text-ink-900">{c.name}</span>
              <span className="text-xs text-ink-500">Category</span>
            </button>
          ))}
          {hits.brands?.map((b) => (
            <button key={`b-${b.slug}`} type="button" onClick={() => go(`/shop/search?brand=${encodeURIComponent(b.slug)}`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-muted">
              <Sparkles className="h-4 w-4 text-ink-400" />
              <span className="flex-1 truncate text-ink-900">{b.name}</span>
              <span className="text-xs text-ink-500">Brand</span>
            </button>
          ))}
          {hits.products?.map((p) => (
            <button key={`p-${p.slug}`} type="button" onClick={() => go(`/shop/p/${p.slug}`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-muted">
              <Search className="h-4 w-4 text-ink-400" />
              <span className="flex-1 truncate text-ink-900">{p.title}</span>
            </button>
          ))}
        </div>
      ) : null}
    </form>
  );
};

/* ----------------------------------------------------------------- shell */

const BarLink = ({ to, icon: Icon, label, badge }) => (
  <Link
    to={to}
    aria-label={badge ? `${label}, ${badge} items` : label}
    className="lk-focus relative flex h-11 shrink-0 items-center gap-2 rounded-xl px-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-surface-muted hover:text-ink-900 md:px-3"
  >
    <span className="relative">
      <Icon className="h-5 w-5" />
      {badge > 0 && (
        <span className="tabular absolute -right-2.5 -top-2 min-w-[18px] rounded-full bg-danger px-1 text-center text-[10px] font-bold leading-[18px] text-white ring-2 ring-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </span>
    <span className="hidden lg:inline">{label}</span>
  </Link>
);

/**
 * Site header + the shop's own bar (search, wishlist, orders, cart) + footer.
 * Every /shop page renders inside this, so the bar never drifts between pages.
 */
export const ShopChrome = ({ children, hideBar = false }) => {
  const { cartCount } = useShopState();
  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />
      {!hideBar && (
        <div className="border-b border-ink-200/70 bg-white">
          <div className="lk-container flex items-center gap-2 py-2.5 md:gap-4">
            <Link to="/shop" className="lk-focus hidden shrink-0 items-center gap-2 rounded-lg pr-2 sm:flex">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-primary text-white">
                <ShoppingBag className="h-5 w-5" />
              </span>
              <span className="lk-heading text-lg leading-none">
                LK <span className="text-brand-secondary">Shop</span>
              </span>
            </Link>
            <ShopSearch />
            <nav className="flex shrink-0 items-center" aria-label="Shop">
              <BarLink to="/shop/wishlist" icon={Heart} label="Wishlist" />
              <BarLink to="/shop/orders" icon={Package} label="Orders" />
              <BarLink to="/shop/cart" icon={ShoppingCart} label="Cart" badge={cartCount} />
            </nav>
          </div>
        </div>
      )}
      <main id="main" className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

/** "Shop › Fashion › Men" trail. Items are { name, to? }. */
export const Crumbs = ({ items = [], className = '' }) => (
  <nav aria-label="Breadcrumb" className={`scrollbar-hide flex items-center gap-1 overflow-x-auto whitespace-nowrap text-xs text-ink-500 ${className}`}>
    {items.map((c, i) => (
      <React.Fragment key={`${c.name}-${i}`}>
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden="true" />}
        {c.to && i < items.length - 1 ? (
          <Link to={c.to} className="lk-focus rounded hover:text-ink-900 hover:underline">
            {c.name}
          </Link>
        ) : (
          <span className={i === items.length - 1 ? 'font-medium text-ink-700' : ''}>{c.name}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

/** Signed-out state for pages that belong to the customer. */
export const SignInPrompt = ({ icon: Icon = ShoppingCart, title, message, onLogin }) => (
  <div className="lk-card mx-auto my-10 flex max-w-md flex-col items-center px-6 py-14 text-center">
    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint">
      <Icon className="h-8 w-8 text-brand-secondary" />
    </div>
    <h1 className="text-xl font-semibold text-ink-900">{title}</h1>
    {message && <p className="mt-1 text-sm text-ink-500">{message}</p>}
    <button type="button" onClick={onLogin} className="lk-btn-primary mt-6">
      Log in to continue
    </button>
  </div>
);

export default ShopChrome;
